import React, { useCallback, useEffect, useState } from 'react';
import { MdArrowBack, MdRefresh, MdDeleteForever, MdCheckCircle } from 'react-icons/md';
import { getMemory, clearCustomerMemory } from '../services/backendAPI';

const ADVISOR_STYLE_KEY = 'swipe_advisor_style';

const DemoStateScreen = ({ onBack, customerId, chatHistoryLength = 0, onClearChat }) => {
  const [memory, setMemory]         = useState(null);
  const [loading, setLoading]       = useState(false);
  const [styleLabel, setStyleLabel] = useState(() => localStorage.getItem(ADVISOR_STYLE_KEY));
  const [flash, setFlash]           = useState(null); // { type: 'ok'|'err', text }

  const notify = (type, text) => {
    setFlash({ type, text });
    setTimeout(() => setFlash(null), 3000);
  };

  const fetchMemory = useCallback(async () => {
    if (!customerId) { setMemory(null); return; }
    setLoading(true);
    const m = await getMemory(customerId);
    setMemory(m);
    setLoading(false);
  }, [customerId]);

  useEffect(() => { fetchMemory(); }, [fetchMemory]);

  const refreshStyle = () => setStyleLabel(localStorage.getItem(ADVISOR_STYLE_KEY));

  const handleClearStyle = () => {
    localStorage.removeItem(ADVISOR_STYLE_KEY);
    refreshStyle();
    notify('ok', 'Advisor style preference cleared.');
  };

  const handleClearChat = () => {
    onClearChat?.();
    notify('ok', 'Chat history cleared.');
  };

  const handleClearMemory = async () => {
    if (!customerId) { notify('err', 'No profile loaded — nothing to clear.'); return; }
    try {
      await clearCustomerMemory(customerId);
      await fetchMemory();
      notify('ok', 'Server memory cleared and re-bootstrapped.');
    } catch (e) {
      notify('err', `Clear failed: ${e.message}`);
    }
  };

  const handleFullReset = async () => {
    localStorage.removeItem(ADVISOR_STYLE_KEY);
    refreshStyle();
    onClearChat?.();
    if (customerId) {
      try {
        await clearCustomerMemory(customerId);
        await fetchMemory();
      } catch (e) {
        notify('err', `Memory clear failed: ${e.message}`);
        return;
      }
    }
    notify('ok', 'Full reset done — clean slate for demo.');
  };

  const memSections = memory ? [
    { label: 'About Me',    items: (memory.aboutMe     || []).map(f => f.fact) },
    { label: 'Likes',       items: (memory.likes        || []).map(l => l.item + (l.note ? ` (${l.note})` : '')) },
    { label: 'Dislikes',    items: (memory.dislikes     || []).map(d => d.item + (d.note ? ` (${d.note})` : '')) },
    { label: 'Goals',       items: (memory.goals        || []).map(g => g.goal) },
    { label: 'Concepts',    items: (memory.concepts     || []).map(c => `${c.label}: ${(c.rules || []).map(r => r.match?.merchant || r.match?.category || '?').join(', ')}`) },
    { label: 'Style',       items: (() => { const s = memory.interactionStyle || {}; return ['tone','length','detail'].filter(k => s[k]).map(k => `${k}: ${s[k]}`); })() },
    { label: 'Assumptions', items: (memory.understanding?.assumptions || []).map(a => a.text) },
  ].filter(s => s.items.length > 0) : [];

  const s = {
    section: { marginBottom: 16 },
    label: { fontSize: 11, fontWeight: 700, color: 'var(--td-green, #008a0d)', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 4 },
    item: { fontSize: 13, color: 'var(--text-primary, #1a1a1a)', lineHeight: 1.5, paddingLeft: 10, borderLeft: '2px solid var(--border, #e8e8e8)', marginBottom: 3 },
    row: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 14px', background: 'var(--surface, #fff)', borderRadius: 10, border: '1px solid var(--border, #e8e8e8)', marginBottom: 8 },
    rowLabel: { fontSize: 13, fontWeight: 600, color: 'var(--text-primary, #1a1a1a)' },
    rowSub: { fontSize: 11, color: 'var(--text-secondary, #666)', marginTop: 2 },
    clearBtn: { fontSize: 11, padding: '5px 12px', borderRadius: 20, border: '1px solid var(--td-red, #d32f2f)', color: 'var(--td-red, #d32f2f)', background: 'transparent', cursor: 'pointer', fontWeight: 600, flexShrink: 0 },
  };

  return (
    <div className="screen bh-flow">
      <header className="bh-flow-hero">
        <div className="bh-flow-hero-left">
          <button className="bh-flow-back" onClick={onBack} aria-label="Back">
            <MdArrowBack />
          </button>
          <h1 className="bh-flow-title">Demo State</h1>
        </div>
        <button className="bh-flow-back" onClick={() => { fetchMemory(); refreshStyle(); }} aria-label="Refresh" title="Refresh">
          <MdRefresh />
        </button>
      </header>

      <div className="bh-flow-body">
        {flash && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 10, marginBottom: 14,
                        background: flash.type === 'ok' ? 'var(--td-green-bg, #e8f5e9)' : 'var(--td-red-bg, #fce4e4)',
                        color: flash.type === 'ok' ? 'var(--td-green, #1b5e20)' : 'var(--td-red, #c62828)', fontSize: 13 }}
               role="status">
            {flash.type === 'ok' ? <MdCheckCircle size={16} /> : <MdDeleteForever size={16} />}
            {flash.text}
          </div>
        )}

        {/* Quick-reset panel */}
        <section style={s.section}>
          <h2 className="bh-section-title" style={{ paddingLeft: 0 }}>Reset Controls</h2>

          <div style={s.row}>
            <div>
              <div style={s.rowLabel}>Advisor Style</div>
              <div style={s.rowSub}>{styleLabel ? `Stored: "${styleLabel}"` : 'Not set — will ask on next flow'}</div>
            </div>
            <button style={s.clearBtn} onClick={handleClearStyle} disabled={!styleLabel}>Clear</button>
          </div>

          <div style={s.row}>
            <div>
              <div style={s.rowLabel}>Chat History</div>
              <div style={s.rowSub}>{chatHistoryLength === 0 ? 'Empty' : `${chatHistoryLength} message${chatHistoryLength !== 1 ? 's' : ''} in session`}</div>
            </div>
            <button style={s.clearBtn} onClick={handleClearChat} disabled={chatHistoryLength === 0}>Clear</button>
          </div>

          <div style={s.row}>
            <div>
              <div style={s.rowLabel}>Server Memory</div>
              <div style={s.rowSub}>
                {!customerId ? 'No profile loaded' :
                 loading ? 'Loading…' :
                 !memory ? 'No memory yet' :
                 `${memSections.length} section${memSections.length !== 1 ? 's' : ''} learned`}
              </div>
            </div>
            <button style={s.clearBtn} onClick={handleClearMemory} disabled={!customerId || loading}>Clear</button>
          </div>

          <button
            onClick={handleFullReset}
            style={{ width: '100%', padding: '13px', borderRadius: 12, border: 'none',
                     background: 'var(--td-red, #d32f2f)', color: '#fff',
                     fontSize: 14, fontWeight: 700, cursor: 'pointer', marginTop: 6,
                     display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <MdDeleteForever size={18} />
            Full Reset — Clear Everything
          </button>
        </section>

        {/* Memory viewer */}
        {customerId && (
          <section style={s.section}>
            <h2 className="bh-section-title" style={{ paddingLeft: 0 }}>
              What IVA Has Learned
              {memory?.updatedAt && (
                <span style={{ fontSize: 10, fontWeight: 400, color: 'var(--text-secondary, #666)', marginLeft: 8 }}>
                  updated {String(memory.updatedAt).slice(0, 10)}
                </span>
              )}
            </h2>

            {loading && !memory && <p className="bh-muted">Loading…</p>}
            {!loading && !memory && <p className="bh-muted">No memory learned yet — start a chat to see it populate.</p>}

            {memSections.map(sec => (
              <div key={sec.label} style={s.section}>
                <div style={s.label}>{sec.label}</div>
                {sec.items.map((item, i) => (
                  <div key={i} style={s.item}>{item}</div>
                ))}
              </div>
            ))}

            {memory && memSections.length === 0 && (
              <p className="bh-muted">Memory file exists but no facts learned yet.</p>
            )}
          </section>
        )}

        {!customerId && (
          <p className="bh-muted" style={{ textAlign: 'center', paddingTop: 16 }}>
            Load a profile from Demo Nav to inspect and reset its memory.
          </p>
        )}
      </div>
    </div>
  );
};

export default DemoStateScreen;
